
import { LOAD_END, LOAD_START } from 'ui/loading-event-type';

import { ClassicWebWorkerApi } from 'cadenza/map/interactions/classic/classic-geometry-edit-strategy/classic-web-worker-api';
import { convertRectangleOrCircleToPolygonOrMultiPolygon } from 'cadenza/map/geom/geometry-converter';
import type { Rectangle } from 'cadenza/map/geom/rectangle';
import { ClassicMergeParams, ClassicValidationParams } from 'cadenza/map/interactions/classic/classic-geometry-edit-strategy/classic-union-without-intersection-utils';
import { assert } from 'cadenza/utils/custom-error';
import {
  geoJsonGeometryToOlGeometry,
  olGeometryToGeoJsonGeometry
} from 'cadenza/utils/geo-json';

import type { Circle, MultiPolygon, Polygon } from 'ol/geom';
import type { Feature } from 'ol';
import type { MultiPolygon as GeoJSONMultiPolygon, Polygon as GeoJSONPolygon } from 'geojson';

let instance: ClassicUnionWithoutIntersectionWorkerApi;

type PolygonAssignable = Polygon | MultiPolygon | Rectangle | Circle;

class ClassicUnionWithoutIntersectionWorkerApi extends ClassicWebWorkerApi {

  _eventBus;

  constructor (worker: Worker, eventBus: EventTarget) {
    super(worker);
    this._eventBus = eventBus;
  }

  async mergeGeometries (currentGeometries: PolygonAssignable[], newGeometry: PolygonAssignable) {
    this._eventBus.dispatchEvent(new CustomEvent(LOAD_START));
    const mergeParams = new ClassicMergeParams(currentGeometries.map(serializeToPolygonOrMulti), serializeToPolygonOrMulti(newGeometry));
    const serializedMergedGeometry = await getWebWorkerInstance().submit(mergeParams)
      .finally(() => {
        this._eventBus.dispatchEvent(new CustomEvent(LOAD_END));
      }) as GeoJSONMultiPolygon | null;
    return geoJsonGeometryToOlGeometry(serializedMergedGeometry ?? undefined);
  }

  async areModificationsValid (featuresBeingModified: Feature<PolygonAssignable>[], allFeatures: Feature<PolygonAssignable>[]) {
    this._eventBus.dispatchEvent(new CustomEvent(LOAD_START));
    const allOtherGeometries = allFeatures.filter(feature => {
      return feature.getGeometry() && !featuresBeingModified.includes(feature);
    }).map(feature => serializeToPolygonOrMulti(feature.getGeometry()!)); // undefined are filtered out above

    // features being modified always have a geometry
    const geometriesBeingModified = featuresBeingModified.map(f => serializeToPolygonOrMulti(f.getGeometry()!));

    const input = new ClassicValidationParams(geometriesBeingModified, allOtherGeometries);
    return await getWebWorkerInstance().submit(input)
      .finally(() => {
        this._eventBus.dispatchEvent(new CustomEvent(LOAD_END));
      }) as boolean;
  }

}

// geometry can contain Circle or Rectangle so we need to transform them to Polygon/MultiPolygon, since they are not standard,
// otherwise serialization will not work, and turf does not accept standardized geometries either
function serializeToPolygonOrMulti (geometry: PolygonAssignable) {
  const polygonOrMulti = convertRectangleOrCircleToPolygonOrMultiPolygon(geometry);
  return olGeometryToGeoJsonGeometry(polygonOrMulti) as GeoJSONMultiPolygon | GeoJSONPolygon;
}

// The eventBus is only needed when creating the instance for the first time
export function getWebWorkerInstance (eventBus?: EventTarget): ClassicUnionWithoutIntersectionWorkerApi {
  if (!instance) {
    const worker = new Worker(new URL('./classic-union-without-intersection.worker.ts', import.meta.url));
    assert(eventBus, 'The event bus is necessary when creating the worker api');
    instance = new ClassicUnionWithoutIntersectionWorkerApi(worker, eventBus);
  }
  return instance;
}
